import { desktopViewsForBreakpoint } from "@/components/canvas/useSharedCalendarState.ts";
import { panelHeaderControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { useMediaQuery } from "@/hooks/useMediaQuery.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ButtonGroup, ButtonGroupSeparator } from "@/components/ui/button-group.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { addDays, addMonths, addWeeks, subDays, subMonths, subWeeks } from "date-fns";
import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { CalendarViewPicker } from "./CalendarViewPicker.tsx";
import { isTodayVisible } from "./isTodayVisible.ts";
import { TodayButton } from "./TodayButton.tsx";
import type { ViewType } from "./types.ts";

const VIEW_PERIOD_LABELS: Record<ViewType, string> = {
  day: "day",
  // Multi-day's prev/next chevrons step one day at a time (same as single-day),
  // so the period label in the tooltip stays "day".
  "multi-day": "day",
  week: "week",
  month: "month",
};

interface CalendarHeaderProps {
  viewType: ViewType;
  onViewTypeChange: (type: ViewType) => void;
  currentDate: Date;
  onDateChange: (date: Date) => void;
  onGoToToday: () => void;
  /**
   * Opens the calendar-options surface. Both breakpoints offer it, so the full
   * picker appends a "Calendars" entry whenever it is set — but they open
   * different surfaces, which is `CanvasPanel`'s business, not this header's.
   */
  onOpenCalendarOptions?: () => void;
}

export const CalendarHeader = ({
  viewType,
  onViewTypeChange,
  currentDate,
  onDateChange,
  onGoToToday,
  onOpenCalendarOptions,
}: CalendarHeaderProps) => {
  const goToPrevious = () => {
    switch (viewType) {
      case "day":
      case "multi-day":
        onDateChange(subDays(currentDate, 1));
        break;
      case "week":
        onDateChange(subWeeks(currentDate, 1));
        break;
      case "month":
        onDateChange(subMonths(currentDate, 1));
        break;
    }
  };

  const goToNext = () => {
    switch (viewType) {
      case "day":
      case "multi-day":
        onDateChange(addDays(currentDate, 1));
        break;
      case "week":
        onDateChange(addWeeks(currentDate, 1));
        break;
      case "month":
        onDateChange(addMonths(currentDate, 1));
        break;
    }
  };

  const goToToday = () => {
    onDateChange(new Date());
    onGoToToday();
  };

  const period = VIEW_PERIOD_LABELS[viewType];
  const todayVisible = isTodayVisible(currentDate, viewType);
  // Viewport, not container: the coercion this agrees with is keyed on the
  // window, so asking the panel's own width would disagree with it on a wide
  // window holding a narrow pane, and on a narrow window holding a wide one.
  const canWeek = useMediaQuery("lg");

  return (
    // The gap mirrors SplitPanelHeader's own `gap-1 @3xl:gap-1.5` step exactly:
    // this cluster's neighbors (title, account switcher) are spaced by the
    // header's gap, so the controls inside it must use the same rhythm or the
    // Today-to-sliders gap reads wider than sliders-to-switcher.
    <div className="flex flex-1 items-center gap-1 @3xl:gap-1.5">
      {/* Right-justify the whole nav cluster — prev/next/today/view-picker
          sit together on the right edge of the header. */}
      <div className="flex-1" />

      {/* === Compact layout (below @md) === */}

      {/* Split prev / next.
          Hidden in every swipeable view — the grid carousel covers day,
          multi-day, AND month (three instances in the DOM), and the day views
          add the week strip on top. Matches iOS Calendar, which has no header
          chevrons here — and keeps the month title from truncating on phones.
          Only week (reachable on desktop's narrow panes, never on mobile)
          still shows them. */}
      <ButtonGroup
        className={cn(
          "rounded-full bg-accent overflow-hidden @md:hidden",
          viewType !== "week" && "hidden",
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              className="size-10"
              data-statsig-name="calendar-nav-previous"
            >
              <ChevronLeft />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Previous {period}</TooltipContent>
        </Tooltip>
        <ButtonGroupSeparator />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              className="size-10"
              data-statsig-name="calendar-nav-next"
            >
              <ChevronRight />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Next {period}</TooltipContent>
        </Tooltip>
      </ButtonGroup>

      {/* The compact (below @md) TodayButton is rendered by the page in the
          header title cluster so the day badge sits next to the month name. */}

      {/* === Full layout (at @md+) === */}

      {/* Prev / next / today, as one browse unit at the same scale as every
          other icon control in the app's chrome (`panelHeaderControlClass`).
          `gap-0.5` is tighter than the cluster's `gap-2` on purpose: the three
          read as one unit, held clear of the view picker beside them. */}
      <div className="hidden @md:flex items-center gap-0.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              className={panelHeaderControlClass}
              data-statsig-name="calendar-nav-previous"
            >
              <ChevronLeft />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Previous {period}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              className={panelHeaderControlClass}
              data-statsig-name="calendar-nav-next"
            >
              <ChevronRight />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Next {period}</TooltipContent>
        </Tooltip>
        {/* Icon-only here too, so the header carries no button labels. It says
            where you are by its treatment — an outline while today is on screen,
            a filled badge once you have scrolled away from it — so it needs no
            second telling in opacity. Fading the outline on top of that only
            made the resting state read as disabled. */}
        <TodayButton
          onClick={goToToday}
          isTodayVisible={todayVisible}
          className={panelHeaderControlClass}
        />
      </div>

      {/* Mobile "today" day-number badge — lives on the right next to the view
          menu, where the wider layout puts it in the browse group above. */}
      <TodayButton onClick={goToToday} isTodayVisible={todayVisible} className="@md:hidden" />

      {/* Compact trigger (below @md): the sliders icon, opening whichever
          calendar-options surface the host mounted — the sheet on a phone,
          the dialog on a desktop pane too narrow for the full picker. Both
          carry the view rows, so this one control is a complete answer at this
          width.

          Which control shows is the container's decision, not the viewport's.
          Asking the viewport is what broke it: a desktop pane below `@md` got
          neither this trigger (not a phone) nor the full picker (container too
          narrow), leaving Week, Month and the calendars list with no way in.
          The 3-dot picker below is for a host that offers no options surface
          at all. */}
      {onOpenCalendarOptions ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(panelHeaderControlClass, "@md:hidden")}
              aria-label="Open calendar settings"
              data-statsig-name="calendar-options-open"
              onClick={onOpenCalendarOptions}
            >
              <SlidersHorizontal />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Calendar settings</TooltipContent>
        </Tooltip>
      ) : (
        <CalendarViewPicker
          viewType={viewType}
          onViewTypeChange={onViewTypeChange}
          availableViews={["day", "multi-day"]}
          compact
          triggerClassName="@md:hidden"
        />
      )}

      {/* View label dropdown (full, at @md+). Its menu is cut to what the
          calendar will hold at this window width: this picker appears from a
          container `@md`, while Week survives only from a viewport `lg`, so on
          the widths between the two it was offering a choice that resolved
          straight back to Day. */}
      <CalendarViewPicker
        viewType={viewType}
        onViewTypeChange={onViewTypeChange}
        onOpenCalendarOptions={onOpenCalendarOptions}
        availableViews={desktopViewsForBreakpoint(canWeek)}
        triggerClassName="h-7 gap-0.5 px-1.5 hidden @md:inline-flex"
      />
    </div>
  );
};
